import { createFileRoute } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { WorkbenchPage } from "@client/components/workbench/workbench-page";
import type { WorkbenchView } from "@client/lib/workbench-view";
import { workbenchSpaceUserIdAtom } from "@client/atoms/workbench";
import {
  ListConversations,
  getListConversationsQueryKey,
} from "@tubo/api-models/hooks/chat-api.hooks";
import {
  projectList,
  getProjectListQueryKey,
} from "@tubo/api-models/hooks/project-api.hooks";
import {
  listDocGenTasks,
  getListDocGenTasksQueryKey,
} from "@tubo/api-models/hooks/doc-gen-task-api.hooks";
import {
  listHistoryGroups,
  getListHistoryGroupsQueryKey,
} from "@tubo/api-models/hooks/history-group-api.hooks";
import { getGetMyFeaturesQueryKey } from "@tubo/api-models/hooks/tenant-feature-api.hooks";

export interface WorkbenchSearch {
  /** 缺省 home（门户） */
  view?: WorkbenchView;
  /** chat 视图的会话深链 */
  conversationId?: string;
}

function isValidView(value: unknown): value is WorkbenchView {
  return value === "chat" || value === "project" || value === "docgen";
}

function isValidConversationId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

export const Route = createFileRoute("/_app/workbench/")({
  validateSearch: (search: Record<string, unknown>): WorkbenchSearch => ({
    view: isValidView(search.view) ? search.view : undefined,
    conversationId: isValidConversationId(search.conversationId)
      ? search.conversationId
      : undefined,
  }),
  // 悬停预取（侧栏 Link preload="intent" 触发）：「最近工作」四路数据提前进缓存，
  // 全局 staleTime 90s 内进入工作台不再重复请求。不 await——页面立即渲染，数据到了再填充。
  // 项目/文档生成两路与 use-work-history-items 同口径做权限门控，避免越权 403 请求噪音。
  // admin 代看空间时预取跟随目标用户（key 与 hooks 侧一致，__self__/缺省同 key）
  loader: ({ context }) => {
    const { queryClient } = context;
    const spaceUserId = getDefaultStore().get(workbenchSpaceUserIdAtom);
    const ownerId = spaceUserId !== "__self__" ? spaceUserId : undefined;
    // AI 助手未开通时对话/历史列表 403，预取跟随功能点（与 docgen 同口径）
    const hasAiAssistant = !!queryClient
      .getQueryData<{ features: string[] }>(getGetMyFeaturesQueryKey())
      ?.features?.includes("ai_assistant");
    if (hasAiAssistant) {
      void queryClient.prefetchQuery({
        queryKey: getListConversationsQueryKey({ ownerId }),
        queryFn: () => ListConversations({ ownerId }),
      });
      void queryClient.prefetchQuery({
        queryKey: getListHistoryGroupsQueryKey({ ownerId }),
        queryFn: () => listHistoryGroups({ ownerId }),
      });
    }
    const permKeys =
      queryClient.getQueryData<{ keys: string[] }>(["permitted-static-keys"])
        ?.keys ?? [];
    const hasPerm = (key: string) => new Set(permKeys).has(key);
    if (permKeys.length > 0 && hasPerm("projects")) {
      void queryClient.prefetchQuery({
        queryKey: getProjectListQueryKey({ status: "active", ownerId }),
        queryFn: () => projectList({ status: "active", ownerId }),
      });
    }
    const hasDocgenFeature = !!queryClient
      .getQueryData<{ features: string[] }>(getGetMyFeaturesQueryKey())
      ?.features?.includes("doc_generation");
    if (permKeys.length > 0 && hasPerm("doc-generation") && hasDocgenFeature) {
      void queryClient.prefetchQuery({
        queryKey: getListDocGenTasksQueryKey({ ownerId }),
        queryFn: () => listDocGenTasks({ ownerId }),
      });
    }
  },
  component: WorkbenchRoute,
});

function WorkbenchRoute() {
  const search = Route.useSearch();
  return (
    <WorkbenchPage
      view={search.view ?? "home"}
      conversationId={search.conversationId}
    />
  );
}
